import { MouseEventHandler, forwardRef, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import Body4 from 'design-system/Typography/Body/Body4'

import { CaretDown } from 'components/SVG/Caret'
import CloseIcon from 'components/SVG/DeleteX'
import Spacer from 'components/Spacer'

import { IconContainer, IconWithText } from '.'

interface IActiveProp {
  active?: boolean
}

interface IHideProp {
  hide?: boolean
}
export const Button = styled.div<IActiveProp & IHideProp>`
  cursor: pointer;
  min-width: 0;
  padding: 8px 8px; // 9 plus 1px border = 10 - illegal value not in DS
  border-radius: ${props => props.theme.radii[12]}px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${props => props.theme.colors[props.active ? 'teal50' : 'white']};
  border: 1px solid ${props => props.theme.colors[props.active ? 'teal500' : 'neutral100']};

  flex-grow: 1;
  flex-shrink: 1;

  ${props => props.hide && 'display: none;'}

  p {
    padding-top: 1px; /* font correction */
    white-space: nowrap;
    word-break: keep-all;
    overflow: hidden;
    text-overflow: ellipsis;

    // we need to aggressively truncate the text here otherwise the page will get too long
    max-width: 96px;
  }

  @media (min-width: ${props => props.theme.metrics.tablet}px) {
    p {
      max-width: 120px;
    }
  }

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    p {
      max-width: 300px;
    }
  }

  @media (min-width: ${props => props.theme.metrics.desktopMedium}px) {
    p {
      max-width: 100px;
    }
  }

  @media (min-width: ${props => props.theme.metrics.desktopLarge}px) {
    p {
      max-width: 180px;
    }
  }
`

interface IProps {
  active: boolean
  clearable: boolean
  label: string
  hide?: boolean
  renderIcon?: () => JSX.Element
  onClick?: () => void
  onClearSelection: () => void
}

const FilterButton = forwardRef<HTMLDivElement, IProps>(
  ({ active, clearable, hide = false, label, onClick, onClearSelection, renderIcon }, ref) => {
    const theme = useContext(ThemeContext)

    const handleClearSelection: MouseEventHandler<HTMLDivElement> = event => {
      if (active) event.stopPropagation() // allow clicks to open dropdown to bubble up

      if (!active) return

      onClearSelection()
    }

    const onClickProps = onClick ? { onClick } : {}

    return (
      <Button
        ref={ref}
        active={active}
        // Because we hide these at smaller viewport and the state lives in the mounted component,
        // we need to hide them in CSS to prevent the state from being reset when changing the
        // viewport size
        hide={hide}
        {...onClickProps}
      >
        <IconWithText>
          {renderIcon && <IconContainer>{renderIcon()}</IconContainer>}
          <Spacer size={4} />
          <Body4>{label}</Body4>
        </IconWithText>
        <Spacer size={4} />
        <IconContainer onClick={handleClearSelection}>
          {clearable
            ? (
              <CloseIcon
                width={12}
                height={12}
                fill={theme.colors.black}
              />
            )
            : (
              <CaretDown
                width={10}
                height={7}
                fill={theme.colors.black}
              />
            )}
        </IconContainer>
      </Button>
    )
  },
)

export default FilterButton
